import type { AxisSelection } from '@atlas/types'
import { AddAffordance, AxisMultiSelect, CardShell, type FilterAxis, FilterRailSkeleton } from '@atlas/ui-components'
import { Chip, Tag } from '@atlas/ui-primitives'
import { useState } from 'react'
import { ARTICLE_STATUS_LABELS, ARTICLE_TYPE_LABELS, ArticleStatus, ArticleType } from '@/domain/article.types'

/** What the rail says it does — the same heading loaded or loading. */
const RAIL_TITLE = 'Filter articles'

export interface ArticleFilterRailProps {
	typeSelection: readonly ArticleType[]
	onToggleType: (articleType: ArticleType) => void

	statusSelection: readonly ArticleStatus[]
	onToggleStatus: (status: ArticleStatus) => void

	/** Every pickable axis, labelled by the registry; a shown axis carries its facet values. */
	axes: FilterAxis[]
	shownAxisCodes: string[]
	classificationSelection: AxisSelection
	onToggleClassification: (axisCode: string, valueCode: string) => void
	onAddAxis: (axisCode: string) => void
	onRemoveAxis: (axisCode: string) => void
}

/**
 * One filter value. A native button carrying `aria-pressed`, so its selected state
 * is exposed programmatically rather than by styling alone, and Enter and Space
 * activate it through native button behaviour.
 */
function FilterChip({ label, selected, onToggle }: { label: string; selected: boolean; onToggle: () => void }) {
	return (
		<Chip
			as="button"
			type="button"
			variant={selected ? 'primary' : 'muted'}
			size="sm"
			aria-pressed={selected}
			onClick={onToggle}
		>
			{label}
		</Chip>
	)
}

/** One non-axis dimension: a native `fieldset`/`legend` group whose values wrap. */
function FilterChipGroup({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<fieldset className="space-y-2">
			<legend className="text-sm font-semibold text-ds-text">{label}</legend>
			<div className="flex flex-wrap gap-2">{children}</div>
		</fieldset>
	)
}

/** The rail's loading shape — defined beside the rail so the two cannot drift. */
export function ArticleFilterRailSkeleton({ label }: { label?: string }) {
	return <FilterRailSkeleton title={RAIL_TITLE} groupCount={3} label={label} />
}

/**
 * The articles index's filter rail.
 *
 * ONE panel: `Type` and `Status` are not axes, so the rail renders them itself as
 * labelled chip groups, and delegates each axis group to the shared
 * `AxisMultiSelect` the design-system rail is built from. Nesting the shared rail's
 * own card inside this one would read as a status box stacked on a second bordered
 * card rather than as a single surface — the same reason the campaign rail owns its
 * panel.
 *
 * Presentational: every value and callback is a prop. The rail holds no selection
 * and filters nothing.
 *
 * Neither `Type` nor `Status` opens with a selection, and the index then lists
 * everything. Which types a surface shows is that surface's decision, and this
 * surface is the one for finding ANY article — hiding a type here would make an
 * article that exists unfindable.
 */
export function ArticleFilterRail({
	typeSelection,
	onToggleType,
	statusSelection,
	onToggleStatus,
	axes,
	shownAxisCodes,
	classificationSelection,
	onToggleClassification,
	onAddAxis,
	onRemoveAxis,
}: ArticleFilterRailProps) {
	const [adding, setAdding] = useState(false)
	const shownAxes = axes.filter((axis) => shownAxisCodes.includes(axis.code))
	const hiddenAxes = axes.filter((axis) => !shownAxisCodes.includes(axis.code))

	return (
		<CardShell as="aside" className="space-y-5">
			<p className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">{RAIL_TITLE}</p>

			<FilterChipGroup label="Type">
				{Object.values(ArticleType).map((articleType) => (
					<FilterChip
						key={articleType}
						label={ARTICLE_TYPE_LABELS[articleType]}
						selected={typeSelection.includes(articleType)}
						onToggle={() => onToggleType(articleType)}
					/>
				))}
			</FilterChipGroup>

			<FilterChipGroup label="Status">
				{Object.values(ArticleStatus).map((status) => (
					<FilterChip
						key={status}
						label={ARTICLE_STATUS_LABELS[status]}
						selected={statusSelection.includes(status)}
						onToggle={() => onToggleStatus(status)}
					/>
				))}
			</FilterChipGroup>

			{shownAxes.map((axis) => (
				<AxisMultiSelect
					key={axis.code}
					axis={axis}
					selectedCodes={classificationSelection[axis.code] ?? []}
					onToggle={(valueCode) => onToggleClassification(axis.code, valueCode)}
					onRemove={() => onRemoveAxis(axis.code)}
				/>
			))}

			{hiddenAxes.length > 0 && (
				<div className="border-t border-dashed border-ds-border pt-4">
					<AddAffordance
						block
						label="Add variable"
						hint="dimension or attribute"
						onPress={() => setAdding((open) => !open)}
					/>
					{adding && (
						<div className="mt-2 flex flex-wrap gap-1.5">
							{hiddenAxes.map((axis) => (
								<button
									key={axis.code}
									type="button"
									onClick={() => {
										onAddAxis(axis.code)
										setAdding(false)
									}}
								>
									<Tag variant="primary" size="sm">
										{axis.label}
									</Tag>
								</button>
							))}
						</div>
					)}
				</div>
			)}
		</CardShell>
	)
}
